/**
 * Sidebar — the navy app-shell rail (mockup), wired to REAL routes only.
 *
 * Nav items map 1:1 to screens that actually exist and are role-gated exactly as
 * the router gates them (see main.tsx). We deliberately do NOT render dead links
 * for Scorecards / Reports / Leaderboard / Teams — those screens don't exist.
 * (Calibration now exists — CP1 — and is a real manager/admin nav item below.)
 * The effective role (simulated when an admin is impersonating) drives
 * visibility, mirroring AppFrame's prior nav.
 *
 * Collapsible: a chevron toggles a slim icon-only rail; the choice persists in
 * localStorage so it survives reloads. In the extension side-panel (narrow) the
 * rail defaults to collapsed.
 */
import { NavLink } from "react-router-dom";
import type { ComponentType, SVGProps } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useSimulation } from "@/auth/SimulationProvider";
import {
  IconDashboard,
  IconCoaching,
  IconSliders,
  IconRadio,
  IconReview,
  IconSettings,
  IconChevronLeft,
  IconCrown,
  IconTrendUp,
} from "@/components/icons";

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

interface NavItem {
  to: string;
  label: string;
  Icon: IconCmp;
  /** Whether this item is visible to the current viewer. */
  show: boolean;
  /** end=true so a parent path doesn't stay active on child routes. */
  end?: boolean;
}

export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { profile, role: realRole, canViewLiveMonitor, signOut } = useAuth();
  const { effectiveRole, isSimulating, simulatedUser } = useSimulation();
  const role = effectiveRole;
  const isManager = role === "manager" || role === "admin";
  const isAdmin = role === "admin";

  // Live Monitor is admin-only AND gated on the per-user toggle. When simulating,
  // we keep using the admin's own real permission (the simulated user's toggle
  // isn't loaded here) — matches the router's RequireLiveMonitor behavior.
  const showLiveMonitor = isAdmin && canViewLiveMonitor;

  const items: NavItem[] = [
    { to: "/calls", label: "Dashboard", Icon: IconDashboard, show: true },
    { to: "/manager", label: "Coaching", Icon: IconCoaching, show: isManager },
    { to: "/calibration", label: "Calibration", Icon: IconTrendUp, show: isManager },
    { to: "/live-monitor", label: "Live Monitor", Icon: IconRadio, show: showLiveMonitor },
    // ONE consolidated Admin panel (Users & Access + AI tabs + Call-log privacy).
    // Admin-only AND hidden while simulating — an admin acting as someone else
    // shouldn't manage users / edit AI config / the blocklist in that context
    // (mirrors the prior AI Settings rule).
    { to: "/admin", label: "Admin", Icon: IconSliders, show: isAdmin && !isSimulating },
    { to: "/stt-compare", label: "STT Compare", Icon: IconReview, show: isManager && !isSimulating },
    { to: "/api-keys", label: "API Keys", Icon: IconSettings, show: isAdmin && !isSimulating },
    { to: "/account", label: "Account", Icon: IconSettings, show: !isSimulating, end: true },
  ];

  const displayUser = simulatedUser ?? profile;
  const displayName =
    displayUser?.full_name?.trim() || displayUser?.email || "Account";
  const initial = displayName.charAt(0).toUpperCase();

  const itemClass = ({ isActive }: { isActive: boolean }) =>
    [
      "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
      collapsed ? "justify-center px-0" : "",
      isActive
        ? "bg-sidebar-accent text-sidebar-foreground"
        : "text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <aside
      className={[
        "flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200",
        collapsed ? "w-16" : "w-60",
      ].join(" ")}
    >
      {/* Brand */}
      <div
        className={[
          "flex items-center gap-2 border-b border-sidebar-border px-4 py-4",
          collapsed ? "justify-center px-0" : "",
        ].join(" ")}
      >
        <span className="text-brand-gold">
          <IconCrown />
        </span>
        {!collapsed && (
          <span className="text-sm font-bold uppercase tracking-wider">Tint King</span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
        {items
          .filter((i) => i.show)
          .map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              className={itemClass}
              title={collapsed ? i.label : undefined}
            >
              <i.Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{i.label}</span>}
            </NavLink>
          ))}
      </nav>

      {/* User chip + collapse */}
      <div className="border-t border-sidebar-border p-3">
        <div
          className={[
            "flex items-center gap-3 rounded-lg px-1 py-1",
            collapsed ? "justify-center px-0" : "",
          ].join(" ")}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sm font-semibold text-sidebar-foreground">
            {initial}
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{displayName}</p>
              <p className="truncate text-xs capitalize text-sidebar-muted">
                {role ?? "—"}
              </p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => void signOut()}
              className="rounded-md px-2 py-1 text-xs font-medium text-sidebar-muted transition hover:bg-sidebar-accent hover:text-sidebar-foreground"
              title="Sign out"
            >
              Sign out
            </button>
          )}
        </div>

        <button
          onClick={onToggle}
          className={[
            "mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-sidebar-muted transition hover:bg-sidebar-accent hover:text-sidebar-foreground",
            collapsed ? "justify-center px-0" : "",
          ].join(" ")}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <IconChevronLeft
            className={[
              "h-4 w-4 transition-transform",
              collapsed ? "rotate-180" : "",
            ].join(" ")}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
        {realRole !== role && !collapsed && (
          <p className="mt-1 px-3 text-[10px] text-sidebar-muted">simulating</p>
        )}
      </div>
    </aside>
  );
}
